import { Date } from '@/components/form/Date';
import { FormGroup } from '@/components/form/FormGroup';
import {
  convertUTCDateToLocal,
  getTimezoneAbbreviation,
} from '@/sections/attacks/capabilities/utils/timezone';

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  error?: string;
}

/**
 * DateRangePicker - Select start and end dates for scheduled execution
 *
 * @param startDate - ISO date string for schedule start date
 * @param endDate - ISO date string for schedule end date
 * @param onStartDateChange - Callback when start date changes
 * @param onEndDateChange - Callback when end date changes
 * @param error - Optional error message to display
 */
export function DateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  error,
}: DateRangePickerProps) {
  // Calculate minimum dates
  const today = new window.Date().toISOString().split('T')[0];
  const minEndDate = startDate || today;

  return (
    <div className="space-y-4">
      <FormGroup
        label="Start Date (UTC)"
        name="startDate"
        info={{
          text: 'When the scheduled execution should begin. Dates are in UTC timezone.',
        }}
      >
        <div className="space-y-1">
          <Date
            name="startDate"
            value={startDate}
            onChange={e => onStartDateChange(e.target.value)}
            min={today}
            required
            placeholder="Select start date"
          />
          {startDate && (
            <div className="text-chariot-text-tertiary text-xs">
              Local: {convertUTCDateToLocal(startDate)} (
              {getTimezoneAbbreviation()})
            </div>
          )}
        </div>
      </FormGroup>

      <FormGroup
        label="End Date (UTC)"
        name="endDate"
        info={{
          text: 'When the scheduled execution should stop (optional). Dates are in UTC timezone.',
        }}
      >
        <div className="space-y-1">
          <Date
            name="endDate"
            value={endDate}
            onChange={e => onEndDateChange(e.target.value)}
            min={minEndDate}
            placeholder="Select end date (optional)"
          />
          {endDate && (
            <div className="text-chariot-text-tertiary text-xs">
              Local: {convertUTCDateToLocal(endDate)} (
              {getTimezoneAbbreviation()})
            </div>
          )}
        </div>
      </FormGroup>

      {error && <div className="text-sm text-red-500">{error}</div>}
    </div>
  );
}
